import {
  getSentryProvider,
  withSentryProvider
} from '@/app/providers/sentry/lazy'

type SentryRuntimeConfig = {
  dsn?: string
  dist?: string
  release?: string
  logLevel?: string
  enabled?: boolean
  environment?: string
  metricsEnabled?: boolean
  tracesSampleRate?: number
}

export default defineNuxtPlugin({
  name: 'sentry-telemetry',
  dependsOn: ['store'],
  setup() {
    const route = useRoute()
    const runtimeConfig = useRuntimeConfig()
    const sharedGeoStore = useSharedGeoStore()
    const sharedWalletStore = useSharedWalletStore()
    const sentryConfig = runtimeConfig.public.sentry as SentryRuntimeConfig

    withSentryProvider(({ configureSentryTelemetry }) => {
      configureSentryTelemetry({
        logLevel: sentryConfig.logLevel,
        isEnabled: Boolean(sentryConfig.enabled && sentryConfig.dsn),
        isMetricsEnabled: Boolean(sentryConfig.metricsEnabled),
        appContext: {
          gitSha: runtimeConfig.public.gitSha,
          appEnv: runtimeConfig.public.appEnv,
          appNetwork: runtimeConfig.public.appNetwork,
          sentryDist: sentryConfig.dist,
          viteNetwork: import.meta.env.VITE_NETWORK,
          sentryRelease: sentryConfig.release,
          sentryEnvironment: sentryConfig.environment,
          chainId: import.meta.env.VITE_CHAIN_ID,
          ethereumChainId: import.meta.env.VITE_ETHEREUM_CHAIN_ID
        }
      })
    })
    scheduleSentryActivationCheck({
      dist: sentryConfig.dist,
      release: sentryConfig.release,
      environment: sentryConfig.environment,
      isConfigured: Boolean(sentryConfig.enabled && sentryConfig.dsn),
      isMetricsEnabled: Boolean(sentryConfig.metricsEnabled)
    })

    watch(
      () => route.path,
      (routePath) => {
        withSentryProvider(({ setSentryRouteContext }) => {
          setSentryRouteContext(routePath)
        })
      },
      { immediate: true }
    )

    watch(
      () => [
        sharedGeoStore.geoCountry,
        sharedGeoStore.geoContinent,
        sharedGeoStore.vpnDetected,
        sharedGeoStore.browserCountry,
        sharedWalletStore.wallet,
        sharedWalletStore.address,
        sharedGeoStore.vpnCheckedTimestamp,
        sharedWalletStore.authZ?.injectiveAddress,
        sharedWalletStore.injectiveAddress,
        sharedWalletStore.isAutoSignEnabled
      ],
      () => {
        withSentryProvider(({ setSentryUserContext }) => {
          setSentryUserContext({
            wallet: sharedWalletStore.wallet,
            geoCountry: sharedGeoStore.geoCountry,
            evmAddress: sharedWalletStore.address,
            browserCountry: sharedGeoStore.browserCountry,
            autoSignEnabled: sharedWalletStore.isAutoSignEnabled,
            authZAddress: sharedWalletStore.authZ?.injectiveAddress,
            geoContinent: sharedGeoStore.geoContinent,
            vpnDetected: sharedGeoStore.vpnDetected,
            vpnCheckedTimestamp: sharedGeoStore.vpnCheckedTimestamp,
            injectiveAddress: sharedWalletStore.injectiveAddress
          })
        })
      },
      { immediate: true }
    )
  }
})

function scheduleSentryActivationCheck(context: {
  dist?: string
  release?: string
  environment?: string
  isConfigured: boolean
  isMetricsEnabled: boolean
}) {
  const checkSentryActivation = async () => {
    const { isSentryTelemetryActive } = await getSentryProvider().catch(
      () => ({ isSentryTelemetryActive: () => false })
    )

    if (!context.isConfigured || isSentryTelemetryActive()) {
      return
    }

    console.warn('[SentryTelemetry] configured but SDK is inactive', context)
  }

  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(checkSentryActivation, { timeout: 1000 })

    return
  }

  setTimeout(checkSentryActivation, 0)
}
